import { Link } from "react-router-dom";
import { useState } from "react";
import PageHero from "@/components/feature/PageHero";
import CTASection from "@/components/feature/CTASection";
import { residentialServices, commercialServices, type ServiceItem } from "@/mocks/services";

type Tab = "residential" | "commercial";

function ServiceCard({ s }: { s: ServiceItem }) {
  return (
    <article
      className="group relative flex flex-col rounded-2xl overflow-hidden bg-background-50 border border-background-200 hover:border-background-300 transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-background-200">
        <img
          src={s.image}
          alt={`${s.title} — local electrician service`}
          loading="lazy" decoding="async"
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-50/90 backdrop-blur-sm text-[11px] text-foreground-800 uppercase tracking-[0.14em]">
          <i className={`${s.icon} text-accent-600`}></i>
          {s.category === "residential" ? "Home" : "Business"}
        </div>
        {s.from && (
          <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-primary-950 text-background-50 text-xs font-semibold">
            From {s.from}
          </div>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="text-lg font-heading font-semibold text-foreground-950">{s.title}</h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{s.description}</p>
        <div className="mt-5 flex items-center justify-between">
          <Link
            to="/book"
            className="group/btn inline-flex items-center gap-1.5 text-sm font-semibold text-primary-800 hover:text-primary-950 transition-colors cursor-pointer whitespace-nowrap"
          >
            Book this service
            <i className="ri-arrow-right-line transition-transform group-hover/btn:translate-x-0.5"></i>
          </Link>
          <Link
            to="/contact"
            aria-label={`Ask about ${s.title}`}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-800 hover:bg-accent-500 hover:text-primary-950 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-up-line"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function ServicesPage() {
  const [tab, setTab] = useState<Tab>("residential");
  const list = tab === "residential" ? residentialServices : commercialServices;

  return (
    <>
      <PageHero
        eyebrow="What I do"
        title={
          <>
            Electrical work,<br />
            done <em className="not-italic text-accent-400">tidy and safe.</em>
          </>
        }
        intro="Homes, flats, landlords and small businesses across Islington and nearby North London — no confusing jargon, no surprise pricing."
        image="https://readdy.ai/api/search-image?query=Independent%20UK%20electrician%20in%20navy%20workwear%20working%20on%20a%20clean%20white%20consumer%20unit%20inside%20a%20London%20terraced%20home%20hallway%20with%20warm%20natural%20light%2C%20detailed%20realistic%20editorial%20photography%2C%20soft%20shadows&width=1400&height=900&seq=services-hero-01&orientation=landscape"
        imageAlt="Islington electrician working on a consumer unit"
        primaryLabel="Book a service"
        secondaryLabel="View pricing"
        secondaryTo="/pricing"
      />

      <section className="px-4 md:px-8 py-12 md:py-20 bg-background-50">
        <div className="flex items-center justify-center mb-8 sm:mb-10">
          <div className="inline-flex items-center p-1 rounded-full bg-background-100 border border-background-200 max-w-full">
            <button
              type="button"
              onClick={() => setTab("residential")}
              className={`px-3 sm:px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                tab === "residential" ? "bg-primary-950 text-background-50" : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              Homes &amp; flats
            </button>
            <button
              type="button"
              onClick={() => setTab("commercial")}
              className={`px-3 sm:px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                tab === "commercial" ? "bg-primary-950 text-background-50" : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              Small businesses
            </button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto text-center mb-10 md:mb-14">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-heading font-semibold tracking-tight text-foreground-950 leading-[1.05]">
            {tab === "residential" ? (
              <>Homes &amp; flats — <em className="not-italic text-accent-600">everyday electrical work.</em></>
            ) : (
              <>Small business support — <em className="not-italic text-accent-600">without the big-company feel.</em></>
            )}
          </h2>
          <p className="mt-4 text-foreground-600 max-w-2xl mx-auto">
            {tab === "residential"
              ? "From a dead socket to a full EICR — quick, clean, and clearly priced."
              : "Local shops, cafés and small offices. I focus on independent businesses, not large corporate projects."}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
          {list.map((s) => (
            <ServiceCard key={s.id} s={s} />
          ))}
        </div>
      </section>

      <section className="px-4 md:px-8 py-12 sm:py-16 md:py-24 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          <div className="lg:col-span-1 flex flex-col justify-center p-6 sm:p-8 md:p-10 rounded-2xl bg-primary-950 text-background-50">
            <span className="text-xs uppercase tracking-[0.18em] text-accent-400 mb-3">
              Emergency
            </span>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-heading font-semibold leading-tight">
              Something looks unsafe?
            </h3>
            <p className="mt-3 text-sm text-background-200 leading-relaxed">
              If you can smell burning, see sparks, or have exposed live wires,
              turn off the power if safe to do so and call emergency services first.
            </p>
            <a
              href="tel:+447700900123"
              className="mt-6 inline-flex self-start items-center gap-2 px-5 py-3 rounded-full bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i>
              Call the electrician
            </a>
          </div>

          <div className="lg:col-span-2 rounded-2xl overflow-hidden bg-background-50 border border-background-200 flex flex-col md:flex-row">
            <div className="relative w-full md:w-1/2 aspect-[4/3] md:aspect-auto md:min-h-[320px]">
              <img
                src="https://readdy.ai/api/search-image?query=Independent%20UK%20electrician%20carefully%20fitting%20a%20new%20pendant%20light%20in%20a%20warm%20London%20living%20room%20with%20wooden%20floors%20and%20soft%20evening%20light%2C%20realistic%20editorial%20photography%2C%20detailed%20texture&width=720&height=640&seq=service-highlight-01&orientation=squarish"
                alt="Local electrician working in a London flat"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex-1 p-5 sm:p-6 md:p-10 flex flex-col justify-center">
              <span className="text-xs uppercase tracking-[0.18em] text-foreground-600 mb-3">
                Landlords &amp; small businesses
              </span>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-heading font-semibold text-foreground-950 leading-tight">
                Reports, certificates and quiet maintenance.
              </h3>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed max-w-md">
                Full EICR paperwork, PAT testing guidance and regular maintenance
                visits — designed to fit around your tenants or business hours.
              </p>
              <div className="mt-6">
                <Link
                  to="/book"
                  className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary-950 text-background-50 text-sm font-semibold hover:bg-primary-900 transition-all hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
                >
                  Book a visit
                  <i className="ri-arrow-right-up-line transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Ready when you are"
        title={
          <>
            Not sure which service<br />
            <em className="not-italic text-accent-600">you need?</em>
          </>
        }
        subtitle="Send a few details and I'll point you in the right direction — no pressure."
        primaryLabel="Book a visit"
        secondaryLabel="Send a message"
        secondaryTo="/contact"
      />
    </>
  );
}